import type { CollectionName } from '../store/types'

/**
 * Mapping between the local camelCase model and the snake_case Postgres
 * columns, declared once per collection so pull and push cannot drift apart.
 *
 * **Only fields listed here cross the wire**, and anything listed here must
 * exist as a column — that is the contract `CLAUDE.md` §1's checklist and
 * `supabase/migrations/0002_schema_guard.sql` both hang off. A field added to
 * one and not the other is either a value that silently never syncs or a push
 * that fails with `PGRST204`.
 *
 * `id`, `household_id`, `updated_at` and `deleted_at` are the envelope and
 * never appear in these lists.
 */

export interface TableSpec {
  /** Postgres table name. */
  table: string
  /** localField -> remoteColumn for everything except the envelope. */
  fields: Readonly<Record<string, string>>
}

export const TABLES: Readonly<Record<CollectionName, TableSpec>> = {
  completions: {
    table: 'completions',
    fields: {
      taskDate: 'task_date',
      taskId: 'task_id',
      memberId: 'member_id',
      done: 'done',
    },
  },

  // The household's own chores (`supabase/migrations/0005_custom_tasks.sql`).
  // Definitions rather than events, and the first table here where two people
  // editing the same row is a real prospect — `docs/architecture-review.md` §2
  // flags that last-write-wins is obviously right for a checkbox and not
  // obviously right for a chore definition. It is what this table gets for now,
  // because the alternative is per-field merging for a row that is edited about
  // twice a year, and the loser of a conflict here is a label, not a checkmark.
  tasks: {
    table: 'tasks',
    fields: {
      label: 'label',
      emoji: 'emoji',
      category: 'category',
      schedule: 'schedule',
      weekday: 'weekday',
      cycleWeek: 'cycle_week',
      onDate: 'on_date',
      assigneeId: 'assignee_id',
    },
  },

  // The people in the household (`0006_members_and_invites.sql`).
  //
  // **`user_id` is deliberately absent, and its absence is the security
  // property.** `toRemote` sends only what is listed here, and a PostgREST
  // upsert sets only the columns in the payload — so no client can ever write
  // the column that says which login a member is. It is written server-side
  // alone, by `claim_membership`, and only for somebody who has just proved
  // control of the address the invitation went to. Adding it to this map would
  // hand every device in the house the ability to seat a stranger at the table.
  //
  // The database says the same thing a second time, in column grants, because
  // this file ships inside a bundle that anybody can read and edit.
  members: {
    table: 'household_members',
    fields: {
      displayName: 'display_name',
      username: 'username',
      authEmail: 'auth_email',
      role: 'role',
    },
  },
}

/**
 * The settings row.
 *
 * A singleton per household with a fixed id, as in `../semainier` — a generated
 * id would let two devices each create their own "the" settings row. It is not
 * in `TABLES` because it is one row rather than a collection, so the driver
 * handles it with its own push/pull pair.
 */
export const SETTINGS_TABLE = 'settings'
export const SETTINGS_ROW_ID = 'settings'
export const SETTINGS_FIELDS = {
  reminderEnabled: 'reminder_enabled',
  reminderTime: 'reminder_time',
  timezone: 'timezone',
} as const

type Row = Record<string, unknown>

/**
 * Local row -> Postgres row.
 *
 * Two departures from `../semainier`, both forced by this project's schema:
 *
 *   - `household_id` is sent explicitly. In semainier the equivalent column
 *     defaults to `auth.uid()` and the client never names it; there is no
 *     equivalent default for a household, so the client sends it and the RLS
 *     policy is what checks it (`supabase/migrations/0001_initial_schema.sql`).
 *
 *   - `updated_at` is **not** sent. semainier stamps the local clock into the
 *     payload and lets the trigger overwrite it; sending a value the server is
 *     guaranteed to discard just invites someone to start trusting it. Both
 *     devices must compare one clock, and that clock is Postgres's —
 *     `CLAUDE.md` §3.
 */
export function toRemote(
  fields: Readonly<Record<string, string>>,
  local: Row,
  householdId: string,
): Row {
  const out: Row = {
    id: local['id'],
    household_id: householdId,
    deleted_at: local['deletedAt'] ?? null,
  }
  for (const [localKey, column] of Object.entries(fields)) {
    out[column] = local[localKey]
  }
  return out
}

/**
 * Postgres timestamps as the local model spells them.
 *
 * PostgREST renders `timestamptz` as `2026-08-23T10:05:00.123456+00:00`, while
 * everything written locally is `new Date().toISOString()` —
 * `2026-08-23T10:05:00.123Z`. Merging compares those strings, and the two
 * spellings do not sort against each other reliably, so every timestamp
 * arriving from the server is canonicalised on the way in. Truncating the
 * microseconds rounds *down*, which at worst re-pulls a row next time round;
 * rounding up would skip it for good.
 */
export function toIsoString(value: unknown): string | null {
  if (typeof value !== 'string' || value === '') return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString()
}

/** Postgres row -> local row, with the sync envelope reset to "clean". */
export function toLocal(fields: Readonly<Record<string, string>>, remote: Row): Row {
  const out: Row = {
    id: remote['id'],
    updatedAt: toIsoString(remote['updated_at']) ?? '',
    deletedAt: toIsoString(remote['deleted_at']),
    dirty: false,
  }
  for (const [localKey, column] of Object.entries(fields)) {
    out[localKey] = remote[column]
  }
  return out
}
